import type { ViewContainerRef } from '@angular/core';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { CurrentUserSelectors } from '@core/account-data';
import { filterBoolean, filterDefined } from '@core/common-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { TutorialDialogService } from '@core/product-updates-legacy';
import { SubscriptionService } from '@recruitee/company-logic';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';

@Injectable({ providedIn: 'root' })
export class RequisitionsTutorialDialogService {
  private readonly tutorialDialogService = inject(TutorialDialogService);
  private readonly store = inject(Store);
  private readonly subscription = inject(SubscriptionService);

  private requisitionsTutorialIntroUsed: boolean;
  private requisitionsTutorialJobUsed: boolean;
  private requisitionsEnabled: boolean;
  private isTrial: boolean;

  constructor() {
    const subscription = this.subscription;

    combineLatest([
      this.store.select(CurrentUserSelectors.data).pipe(filterDefined),
      this.store.select(RequisitionsConfigSelectors.requisitionsFeatureEnabled),
    ])
      .pipe(filterBoolean)
      .subscribe(([currentAdmin, requisitionsEnabled]) => {
        this.requisitionsEnabled = requisitionsEnabled;
        this.requisitionsTutorialIntroUsed =
          currentAdmin.featureDiscovery.requisitionsTutorialIntroUsed;
        this.requisitionsTutorialJobUsed =
          currentAdmin.featureDiscovery.requisitionsTutorialJobUsed;
        this.isTrial = subscription.isTrial(); // temp check, until requisitions are only available on lead plan
      });
  }

  public openIntroDialogIfNeeded(vcRef: ViewContainerRef) {
    if (!this.requisitionsEnabled || this.requisitionsTutorialIntroUsed || this.isTrial) return;

    this.tutorialDialogService
      .open('requisitions_intro', vcRef)
      ?.afterClosed.pipe(take(1))
      .subscribe(() => {
        this.store.dispatch(
          FeatureDiscoveryActions.update({ payload: { requisitionsTutorialIntroUsed: true } }),
        );
      });
  }

  public openJobDialogIfNeeded(vcRef: ViewContainerRef) {
    if (!this.requisitionsEnabled || this.requisitionsTutorialJobUsed || this.isTrial) return;

    this.tutorialDialogService
      .open('requisitions_job', vcRef)
      ?.afterClosed.pipe(take(1))
      .subscribe(() => {
        this.store.dispatch(
          FeatureDiscoveryActions.update({ payload: { requisitionsTutorialJobUsed: true } }),
        );
      });
  }
}
